<template>
  <div>
    <VTableRemote
      ref="autocompleteRef"
      :iconHeader="hasPermission('procedures.add') ? 'add' : null"
      isSearchable
      isSelectable
      :headers="tableHeaders"
      :itemActions="itemActions"
      :items="procedures"
      :title="$t('configuration.procedures')"
      :pagination="pagination"
      hideDefaultFooter
      :isInfinitScroll="true"
      :isCustomIcon="true"
      :centerColumns="['action', 'files']"
      :notSortableColumns="['action', 'files']"
      :sortableColumns="['code', 'name', 'type', 'subType']"
      @iconAction="onClickAction"
      @select-row="openEditionModal($event)"
      @sendQueryParams="fetchQuery"
    >
      <template #item.files="{ item }">
        <TableFiles :files="item.files" />
      </template>
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </VTableRemote>
  </div>
</template>

<script>
import { isEmpty, pick } from 'lodash';
import Vue from 'vue';
import { mapGetters } from 'vuex';
import VTableRemote from '@/components/shared/content-views/VTableRemote';
import ProcedureForm from '@/components/forms/ProcedureForm';
import TableFiles from '@/components/shared/content-views/slots/TableFiles';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import perpage from '@/constants/pagination';

const typeEnum = ['analytic', 'mechanic', 'corrective'];
const subTypeEnum = ['closed-loop', 'cooling-tower', 'domestic-tower', 'steam-boiler'];

export default {
    components: {
        VTableRemote,
        TableFiles,
    },
    data() {
        return {
            procedures: [],
            loading: false,
            pagination: {},
            queryParams: { perPage: perpage },
        };
    },
    computed: {
        ...mapGetters([
            'lang',
            'hasPermission',
            'teamMainLang',
        ]),
        tableHeaders() {
            return [
                'code',
                'name',
                'type',
                'subType',
                'files',
            ].map(header => ({
                text: this.$t(`forms.labels.${header}`),
                value: header,
                sortable: header !== 'files',
            }));
        },
        itemActions() {
            const actions = [];
            if (this.hasPermission('procedures.edit')) {
                actions.push({
                    name: this.$t('forms.btn.edit'),
                    icon: 'edit',
                    mode: 'edit',
                });
            }
            if (this.hasPermission('procedures.remove')) {
                actions.push({
                    name: this.$t('forms.btn.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }
            return actions;
        },
    },
    watch: {
        async lang() {
            this.queryParams = { perPage: 25 };
            await this.fetchQuery();
        },
    },
    async created() {
        this.loading = true;
        await this.getProcedures();
        this.loading = false;
    },
    methods: {
        /**
           * Prepare a procedure for the Data Table component:
           *  - Group files
           *  - Localize the name and type
           *
           * @param {Object} procedure - Data from the API
           * @return {Object}
           */

        prepareProcedure(procedure) {
            const { type, subType } = procedure;

            const name = procedure.name ? procedure?.name[this.lang] || procedure?.name[this.teamMainLang] : '';
            const [selectedFile] = procedure.files ? procedure.files.filter(file => file.lang === (this.lang || this.teamMainLang)) : [];
            const files = [];
            if (selectedFile) {
                files.push({ id: selectedFile.file.id, fileName: selectedFile.file.fileName });
            }
            return {
                ...procedure,
                name,
                files,
                type: this.$t(`forms.labels.${type}`),
                subType: subTypeEnum.includes(subType) ? this.$t(`procedures.${subType}`) : '',
            };
        },
        /**
           * Prepare a file for the Data Table component
           *
           * @param {Object} file - Data from the API
           * @param {String} lang
           * @return {Object}
           */
        prepareFile(file, lang = null) {
            return {
                ...file,
                lang,
            };
        },
        /**
           * @return {Promise}
           */
        async getProcedures() {
            if (isEmpty(this.pagination) || (this.pagination?.currentPage !== this.queryParams?.page)) {
                const { data } = await this.$rq.getProcedures(this.queryParams);
                this.procedures = [...this.procedures, ...data.procedures.map(procedure => this.prepareProcedure(procedure))];
                this.pagination = data.pagination;
            }
        },

        async fetchQuery(queryParams) {
            this.queryParams = { ...this.queryParams, ...queryParams, page: 1 };
            this.pagination = {};
            this.procedures = [];
            await this.getProcedures();
        },
        /**
           * @param {Object} requestPayload
           * @return {Promise}
           */
        async addProcedure(requestPayload) {
            const payload = this.preparePayload(requestPayload);
            await this.$rq.addProcedure(payload);

            this.$toasted.global.toast_success({
                message: this.$t('msg.procedureAdded', { data: requestPayload.code }),
            });
            await this.fetchQuery({});
        },
        /**
           * @param {String} id
           * @param {Object} requestPayload
           * @return {Promise}
           */
        async updateProcedure(id, requestPayload) {
            const payload = this.preparePayload(requestPayload);
            await this.$rq.updateProcedure(id, payload);

            this.$toasted.global.toast_success({
                message: this.$t('msg.procedureUpdated', { data: requestPayload.code }),
            });
            await this.fetchQuery({});
        },
        /**
           * @param {Object} requestPayload
           * @return {FormData}
           */

        preparePayload(requestPayload) {
            const payload = pick(requestPayload, [
                'code',
                'files',
                'name',
                'subType',
                'type',
            ]);

            // Force empty string for the subType if not selected, FormData can't accept null values
            payload.subType = isEmpty(payload.subType) ? '' : payload.subType;
            payload.name = JSON.stringify(payload.name);
            const request = new FormData();
            Object.keys(payload).forEach(key => {
                if (key !== 'files') {
                    request.append(key, payload[key]);
                }
            });
            Object.keys(payload.files).forEach(file => {
                request.append(`file_${file}`, payload.files[file] || '');
            });

            return request;
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('procedures.addProcedure'),
                component: Vue.component('ProcedureForm', ProcedureForm),
                showLangs: true,
                payload: {
                    typeEnum,
                    subTypeEnum,
                },
                onConfirm: requestPayload => this.addProcedure(requestPayload),
            });
        },
        /**
           * @param {Object} item
           * @param {String} action
           * @return {Promise}
           */
        async onClickAction(item, action) {
            if (item === 'add') {
                this.onClickAdd();
            } else if (action.mode === 'edit') {
                await this.openEditionModal(item);
            } else if (action.mode === 'delete') {
                this.handleRemove(item);
            }
        },
        /**
           * @param {Object} procedure
           * @return {Promise}
           */
        async openEditionModal({ id }) {
            const { data } = await this.$rq.getProcedure(id);

            this.$root.$emit('showModal', {
                title: this.$t('procedures.updateProcedure'),
                component: Vue.component('ProcedureForm', ProcedureForm),
                showLangs: true,
                payload: {
                    isEdition: true,
                    procedure: data,
                    subTypeEnum,
                    typeEnum,
                },
                onConfirm: requestPayload => this.updateProcedure(id, requestPayload),
            });
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getProcedures();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
        handleRemove(procedure) {
            this.$root.$emit('showModal', {
                title: this.$t('procedures.remove'),
                component: Vue.component('ConfirmForm', ConfirmForm),
                payload: {
                    msg: this.$t('procedures.confirmRemove', { data: procedure.code }),
                },
                onConfirm: async () => {
                    await this.$rq.deleteProcedure(procedure.id);
                    this.$toasted.global.toast_success({
                        message: this.$t('msg.procedureRemoved', {
                            data: procedure.code,
                        }),
                    });
                    await this.fetchQuery(this.queryParams);
                },
                isForm: false,
            });
        },
    },
};
</script>
